import React from "react";
import { Download, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion"
import event_1 from "../../assets/images/event_1.jpg"
import event_2 from "../../assets/images/event_2.jpg"
import event_3 from "../../assets/images/event_3.jpg"
const eventResources = [
    {
        id: "res-01",
        title: "Camunda 8 & Zeebe Micro-Workers: Production Blueprint",
        date: "October 2026",
        summary:
            "A complete architectural guide to deploying distributed external workers with gRPC stream isolation and resilient boundary events.",
        image:
            event_1,
        keyItems: [
            "Fault-tolerant worker deployment topologies",
            "Deterministic DMN error-boundary configurations",
            "gRPC payload envelope schemas & code snippets",
        ],
        downloadLink: "/assets/keynotes/zeebe-blueprint-2026.pdf",
        fileSize: "4.8 MB PDF",
    },
    {
        id: "res-02",
        title: "Autonomous Prior-Auth: HL7 FHIR Interoperability Deck",
        date: "September 2026",
        summary:
            "Strategies for unifying EHR clinical charts with real-time payer coverage guidelines to satisfy CMS-0057-F mandate deadlines.",
         image:
            event_2,
        keyItems: [
            "Da Vinci CRD & DTR operational workflows",
            "Point-of-care clinical chart extraction patterns",
            "Statutory SLA compliance verification checklist",
        ],
        downloadLink: "/assets/keynotes/fhir-prior-auth-deck.pdf",
        fileSize: "6.2 MB PDF",
    },
    {
        id: "res-03",
        title: "Legacy Reimagined: Event-Driven Banking Modernization",
        date: "August 2026",
        summary:
            "Pragmatic Strangler-Fig execution patterns for bridging AS/400 and SOAP engines directly into high-throughput cloud streaming.",
          image:
            event_3,
        keyItems: [
            "Non-intrusive Change Data Capture (CDC) design",
            "Sub-second transactional ledger reconciliation",
            "Zero-downtime cutover risk mitigation matrix",
        ],
        downloadLink: "/assets/keynotes/banking-modernization-deck.pdf",
        fileSize: "5.1 MB PDF",
    },
];
export default function EventKeynotes() {
    return (
        <section className="w-full bg-[#F8FAFC] py-16 md:py-24 px-4 sm:px-6 lg:px-12 font-sans border-b border-slate-200/80">
            <div className="xl:w-4/5 mx-auto space-y-12">

                <div className="max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.05 }}
                        className="inline-flex items-center gap-2 px-0.5 py-1.5 backdrop-blur-md shadow-xs mb-4"
                    >
                        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                            EVENT COLLATERAL & ARTIFACTS
                        </span>
                    </motion.div>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
                        Download <span className="italic font-serif font-normal text-[#5dc9f0]">Keynotes</span> & {" "}
                        <span className="italic font-serif font-normal text-[#5dc9f0]">
                            Blueprints
                        </span>
                    </h2>
                    <p className="text-slate-600 mt-3 text-base  leading-relaxed">
                        Access slide decks, architecture teardowns, and engineering
                        specifications delivered during our live masterclasses and summits.
                    </p>
                </div>
                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {eventResources.map((item) => (
                        <article className=" group relative border overflow-hidden border-slate-200/90 rounded-xl p-6 flex flex-col justify-between hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 bg-white">
                            <div className="relative z-10 space-y-6">
                                <div className="relative w-full h-56 sm:h-64 rounded-xl overflow-hidden bg-slate-100 border border-slate-200/80">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                                    <div className="absolute bottom-4 left-4">
                                        <span className="font-bold text-xs uppercase tracking-wider text-white px-3 py-1 rounded-md shadow-xs ">
                                            {item.date}
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <h3 className="text-xl sm:text-2xl font-bold text-[#000036] tracking-tight leading-snug  transition-colors">
                                        {item.title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-slate-600 font-normal leading-relaxed">
                                        {item.summary}
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-2 pt-2 mt-2 border-t border-slate-100">
                                <span className="text-sm font-bold uppercase tracking-wider text-slate-400 block mb-2">
                                    What's Included
                                </span>
                                {item.keyItems.map((bullet, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-start gap-2.5 text-sm text-slate-700 font-medium leading-tight"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5 text-[#5dc9f0] shrink-0 mt-0.5" />
                                        <span>{bullet}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="relative z-10 pt-6 mt-6 border-t border-slate-100 flex items-center justify-between">
                                <a
                                    href={item.downloadLink}
                                    download
                                    className="group inline-flex items-center w-auto justify-between gap-3 px-6 py-2.5 border border-black/15 bg-white/70 text-slate-900 font-mono text-sm font-semibold uppercase tracking-widest transition-all duration-300 hover:bg-slate-950 hover:text-white hover:shadow-md rounded-full "
                                >
                                    <span className="flex items-center gap-2">
                                        Download Keynote
                                    </span>
                                    <div className="h-6 w-6 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:translate-y-0.5 group-hover:bg-white group-hover:text-slate-950">
                                        <Download size={13} />
                                    </div>
                                </a>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}